import { FC } from 'react';

const MONTH_ABBR = [
  'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
  'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC',
] as const;

function monthLabel(yearMonth: string): string {
  // yearMonth is "YYYY-MM". Never pass to new Date() — TZ trap.
  const mm = parseInt(yearMonth.split('-')[1] ?? '1', 10);
  return MONTH_ABBR[mm - 1] ?? '';
}

interface MonthlyBarChartProps {
  data: MonthlyBucket[];
}

// SVG canvas: 340 x 180, with baseline at y=150 leaving 120px for bars + 30px for labels.
const VIEW_W = 340;
const VIEW_H = 180;
const BASELINE_Y = 150;
const MAX_BAR_H = 120;
const GROUP_COUNT = 12;
const GROUP_W = 20;
const BAR_W = 7;
const GROUP_GAP = (VIEW_W - GROUP_COUNT * GROUP_W) / (GROUP_COUNT - 1);

export const MonthlyBarChart: FC<MonthlyBarChartProps> = ({ data }) => {
  const peak = Math.max(
    1,
    ...data.map((d) => Math.max(d.lost, d.found)),
  );
  const anyData = data.some((d) => d.lost > 0 || d.found > 0);

  if (!anyData) {
    return (
      <div
        className="nb-hatch flex items-center justify-center py-10 text-[11px] font-bold uppercase tracking-[0.08em] opacity-60"
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          border: '2px dashed var(--nb-ink)',
          background: 'var(--nb-bg)',
          borderRadius: 'var(--nb-radius)',
        }}
      >
        Todavía no hay actividad
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Publicaciones por mes, perdidos y encontrados"
    >
      <rect x={0} y={BASELINE_Y} width={VIEW_W} height={2} fill="var(--nb-ink)" />

      {data.map((bucket, i) => {
        const groupX = i * (GROUP_W + GROUP_GAP);
        const lostH = (bucket.lost / peak) * MAX_BAR_H;
        const foundH = (bucket.found / peak) * MAX_BAR_H;

        return (
          <g key={bucket.month}>
            {bucket.lost > 0 && (
              <rect
                x={groupX}
                y={BASELINE_Y - lostH}
                width={BAR_W}
                height={lostH}
                fill="var(--nb-lost)"
                stroke="var(--nb-ink)"
                strokeWidth={1.5}
                style={{ filter: 'drop-shadow(2px 2px 0 var(--nb-ink))' }}
              />
            )}
            {bucket.found > 0 && (
              <rect
                x={groupX + BAR_W + 1}
                y={BASELINE_Y - foundH}
                width={BAR_W}
                height={foundH}
                fill="var(--nb-found)"
                stroke="var(--nb-ink)"
                strokeWidth={1.5}
                style={{ filter: 'drop-shadow(2px 2px 0 var(--nb-ink))' }}
              />
            )}
            <text
              x={groupX + GROUP_W / 2}
              y={BASELINE_Y + 16}
              textAnchor="middle"
              fontFamily='"JetBrains Mono", monospace'
              fontSize={9}
              fontWeight={700}
              fill="var(--nb-ink)"
            >
              {monthLabel(bucket.month)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
